/* eslint-disable no-unsafe-optional-chaining */

import { ChangeDetectorRef, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormControl, FormArray, Validators } from '@angular/forms';
import { INotes } from 'src/app/features/models/inotes.modal';
import { ICompany } from 'src/app/shared/models/i-company.modal';
import { IPrecastRecord } from 'src/app/shared/models/i-precast-record.modal';
import { ActivatedRoute, Router } from '@angular/router';
import { FormService } from 'src/app/shared/services/form.service';
import { UserService } from 'src/app/shared/services/user.service';
import { ReplaySubject, takeUntil } from 'rxjs';
import { messagesConfig } from 'src/app/shared/constants/message.constants';
import { PrecastService } from 'src/app/shared/services/precast.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { IMessage } from 'src/app/shared/models/message.modal';
import { MessageModalComponent } from 'src/app/shared/components/message-modal/message-modal.component';
import { SharedService } from 'src/app/features/service/shared.service';
import { DatePipe } from '@angular/common';
import { DateTimeUtils } from 'src/app/shared/util/date-time.utils';

interface PaymentTerm {
  id: string;
  label: string;
  order: number;
  disabled: boolean;
}

@Component({
    selector: 'app-accounting',
    templateUrl: './accounting.component.html',
    styleUrls: ['./accounting.component.scss'],
    providers: [DatePipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AccountingComponent implements OnInit {
  soilType = '';
  projectId: string;
  companyInformation: ICompany;
  billingInformation: ICompany;
  projectNumber: any;
  formData: IPrecastRecord;
  public note: INotes | undefined;
  noteText: string | undefined = '';
  form: FormGroup;
  additionalNotes: string;
  additionalNotesError = false;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  pageLeaveDialogRef: MatDialogRef<ConfirmationDialogComponent>;
  response: any;
  notes: INotes[];
  billingId:string;
  samplePayments: PaymentTerm[] = [];
  isNonBillable = false;
  billingData:any;
  updateSuccess: MatDialogRef<MessageModalComponent>;
  isAddRates:boolean;
  userRoles: string[] = [];
  constructor(
    public userService: UserService,
    public activatedRoute: ActivatedRoute,
    public router: Router,
    private snackBar: MatSnackBar,
    private formService: FormService,
    public dialog: MatDialog,
    private precastService: PrecastService,
    private cdRef: ChangeDetectorRef,
    private sharedService: SharedService,
    private route: ActivatedRoute,
  ) {
    this.form = new FormGroup({
      rushTesting: new FormControl(false),
      isNonBillable: new FormControl(false),
      paymentTerms: new FormControl('', Validators.required),
      routeThermalSurvey: new FormControl({
        value: false, disabled: true,
      }),
      invoiceNumber: new FormControl(''),
      invoiceDate: new FormControl('', Validators.required),
      invoiceSent: new FormControl(false),
      reportSent: new FormControl(false),
      projectRates: new FormArray([]),
    });
  }

  ngOnInit(): void {
    this.userService.userRole?.subscribe(
      (roles) => {
        this.userRoles = roles || [];
      },
      (error) => console.error('userRole subscription error:', error),
    );
    const sampleType = this.activatedRoute.snapshot.paramMap.get('sampletype');
    this.projectId = this.activatedRoute.snapshot.paramMap.get('id') || '';
    this.route.queryParams?.subscribe(({ status }) => {
      this.isAddRates = (status === 'Management Approved');
    });

    this.setSoilType(sampleType);
    if (this.projectId) {
      this.sampleLookup();
      this.getCompanydetails(this.projectId, this.soilType);
      this.getbillingDetails(this.projectId);
    }
    this.isNonBillable = this.form.get('isNonBillable')?.value || false;
  }

  roleCheck(roles: string[]): boolean {
    return roles.some((role) => this.userRoles.includes(role));
  }

  sampleLookup() {
    this.precastService.getSampleBillingLookup()?.subscribe({
      next: (res) => {
        if (res && Array.isArray(res)) {
          this.samplePayments = res;
        }
      },
      error: (error) => {
        console.error('Error fetching sample statuses:', error);
      },
    });
  }

  getbillingDetails(projectId:string) {
    this.precastService.getbillingDetails(projectId)?.subscribe({
      next: (data) => {
        if (data) {
          this.billingData = data;
          this.setbillingData(data);
        }
      },
      error: () => {},
    });
  }

  setSoilType(sampleType: string | null): void {
    if (sampleType === 'nativesoil') {
      this.soilType = 'nativesoil';
    } else if (sampleType === 'precast') {
      this.soilType = 'precast';
    } else {
      this.router.navigate(['/']);
    }
  }

  getCompanydetails(projectId: string, soilType: string) {
    this.formService.fetchFormData(projectId, soilType).subscribe({
      next: (data) => {
        if (data) {
          this.setFormData(data);

          this.formService.updateData(this.formData);
        }
      },
      error: () => {},
    });
  }

  setFormData(data: IPrecastRecord): void {
    this.formData = data;
    this.companyInformation = {
      ...this.formData.company,
      ...this.formData.project,
    };
    this.billingInformation = {
      ...this.formData.company,
      ...this.formData.billing,
    };
  }

  get projectRates(): FormArray {
    return (this.form.get('projectRates') as FormArray) || new FormArray([]);
  }

  get invoiceNumber(): string {
    return this.form.get('invoiceNumber')?.value;
  }

  get paymentTerms(): string {
    return this.form.get('paymentTerms')?.value;
  }

  // Function to set the form data based on the API response
  setbillingData(response: any): void {
    this.billingId = response.billing.id;
    // Set the billing data to the form fields
    this.form.patchValue({
      rushTesting: response.billing.rushTesting,
      isNonBillable: response.billing.isNonBillable,
      paymentTerms: response.billing.paymentsTermsTypeId,
      routeThermalSurvey: response.billing.routeThermalSurvey,
      invoiceNumber: response.billing.invoiceNumber,
      invoiceDate: response.billing.invoiceDate ? DateTimeUtils.getLocalDateTimeFromUTCDate(response.billing.invoiceDate) : null,
      invoiceSent: response.billing.invoiceSent,
      reportSent: response.billing.reportSent,
    });
    this.isNonBillable = response.billing.isNonBillable;
    // Initialize the projectRates dynamically based on the response
    this.initializeProjectRates(response.projectRates);

    // ADD RUSH RATE IF PROJECT IS RUSH
    if (response.billing.rushTesting) {
      this.addRushRateIfNeeded();
    }
  }

  addRushRateIfNeeded(): void {
    const projectRatesArray = this.form.get('projectRates') as FormArray;
    // Check if Rush Rate already exists
    const rushRateExists = projectRatesArray.controls.some(
      (control) =>
        control.get('rateName')?.value?.trim()?.toLowerCase() === 'rush testing rate',
    );
    if (rushRateExists) {
      return;
    }

    // Get max sort order
    const maxSortOrder = projectRatesArray.controls.reduce((max, control) => {
      const sortOrder = control.get('sortOrder')?.value || 0;
      return sortOrder > max ? sortOrder : max;
    }, 0);

    // Add Rush Rate
    projectRatesArray.push(
      new FormGroup({
        id: new FormControl(null),
        rateName: new FormControl('Rush Testing Rate', Validators.required),
        qty: new FormControl(null, [Validators.required, Validators.min(1)]),
        rate: new FormControl(null, [Validators.required, Validators.min(0)]),
        amount: new FormControl(0, Validators.required),
        sortOrder: new FormControl(maxSortOrder + 1),
        disable: new FormControl(false),
        routeSurvey: new FormControl(false),
      }),
    );

    this.sortProjectRates();
    this.recalculateAmounts();
  }

  // Function to initialize projectRates FormArray dynamically
  initializeProjectRates(rates: any[]): void {
    const projectRatesArray = this.form.get('projectRates') as FormArray;

    rates.forEach((rate) => {
      projectRatesArray.push(
        new FormGroup({
          id: new FormControl(rate.id || null),
          rateName: new FormControl(rate.rateName, Validators.required),
          qty: new FormControl(rate.quantity, [Validators.required, Validators.min(1)]),
          rate: new FormControl(rate.rate, [Validators.required, Validators.min(0)]),
          amount: new FormControl(rate.quantity * rate.rate, Validators.required),
          sortOrder: new FormControl(rate.sortOrder || 0),
          disable: new FormControl(rate.disable || false),
          routeSurvey: new FormControl(rate.routeSurvey || false),
        }),
      );
    });
    this.sortProjectRates();
    this.recalculateAmounts(); // Recalculate amounts after initializing projectRates
  }

  addRate(): void {
    const projectRatesArray = this.form.get('projectRates') as FormArray;
    // Get the existing project rates
    const existingRates = projectRatesArray.controls;
    // Find the maximum sortOrder value from existing rates
    const maxSortOrder = existingRates.reduce((max, rateGroup) => {
      const sortOrder = rateGroup.get('sortOrder')?.value || 0; // default to 0 if undefined
      return sortOrder > max ? sortOrder : max;
    }, 0);
    // Add a new rate with the next sortOrder
    projectRatesArray.push(
      new FormGroup({
        rateName: new FormControl('New Rate', Validators.required),
        qty: new FormControl(1, [Validators.required, Validators.min(1)]),
        rate: new FormControl(0, [Validators.required, Validators.min(0)]),
        amount: new FormControl(0, Validators.required),
        sortOrder: new FormControl(maxSortOrder + 1), // Set the sortOrder for the new rate
        disable: new FormControl(false),
        routeSurvey: new FormControl(false),
      }),
    );
    this.sortProjectRates();
  }

  sortProjectRates(): void {
    const projectRatesArray = this.form.get('projectRates') as FormArray;
    projectRatesArray.controls.sort((a, b) => {
      const sortOrderA = a.get('sortOrder')?.value;
      const sortOrderB = b.get('sortOrder')?.value;
      return sortOrderA - sortOrderB;
    });
  }

  // Function to recalculate the amount whenever quantity or rate changes
  recalculateAmounts() {
    const projectRatesArray = this.form.get('projectRates') as FormArray;
    projectRatesArray.controls.forEach((control) => {
      const qty = control.get('qty')?.value;
      const rate = control.get('rate')?.value;
      control.get('amount')?.setValue(qty * rate);
    });
  }

  calculateAmount(rateIndex: number): number {
    // Access the 'projectRates' FormArray from the form
    const projectRatesArray = this.form.get('projectRates') as FormArray;
    // Get the specific FormGroup by index
    const rateGroup = projectRatesArray.at(rateIndex);
    // Retrieve the 'qty' value, defaulting to 0 if null or undefined
    const qty = rateGroup.get('qty')?.value || 0;
    // Retrieve the 'rate' value, defaulting to 0 if null or undefined
    const rate = rateGroup.get('rate')?.value || 0;
    // Calculate amount as qty * rate
    return qty * rate;
  }

  // Function to calculate the total amount from all rates

  calculateTotal(): number {
    // Retrieve the 'projectRates' form array from the form
    const projectRatesArray = this.form.get('projectRates') as FormArray;
    // Use reduce to sum the 'amount' values of each control in the form array
    return projectRatesArray.controls.reduce((total, control) => total + control.get('amount')?.value, 0);
  }

  removeRate(index: number): void {
    // Retrieve the FormArray containing project rates from the form
    const projectRatesArray = this.form.get('projectRates') as FormArray;
    // Check if there is more than one rate in the array to ensure at least one rate remains
    if (projectRatesArray.length > 1) {
      // Remove the rate at the specified index
      projectRatesArray.removeAt(index);
      this.sortProjectRates();
    }
  }

  onNotedValue(notes: string): void {
    this.additionalNotes = notes;
  }

  update(): void {
    // Constructing the payload object
    const payload = {
      billing: {
        id: this.billingId,
        projectId: this.projectId,
        rushTesting: this.form.get('rushTesting')?.value,
        isNonBillable: this.form.get('isNonBillable')?.value,
        paymentsTermsTypeId: this.form.get('paymentTerms')?.value,
        routeThermalSurvey: this.form.get('routeThermalSurvey')?.value,
        invoiceNumber: this.form.get('invoiceNumber')?.value,
        invoiceDate: this.form.get('invoiceDate')?.value,
        invoiceSent: this.form.get('invoiceSent')?.value,
        reportSent: this.form.get('reportSent')?.value,
      },
      projectRates: this.projectRates.controls.map((rateGroup) => ({
        id: rateGroup.get('id')?.value || null,
        rateName: rateGroup.get('rateName')?.value,
        quantity: rateGroup.get('qty')?.value,
        rate: rateGroup.get('rate')?.value,
        disable: rateGroup.get('disable')?.value,
        routeSurvey: rateGroup.get('routeSurvey')?.value,
        sortOrder: rateGroup.get('sortOrder')?.value || 0,
      })),
    };

    this.precastService.postbillingDetails(payload).subscribe({
      next: (response) => {
        if (response) {
          const successMessage: IMessage = messagesConfig.accountingSuccessMessage;
          const url = '/dashboard';
          this.showUpdatePopUpMessage(successMessage, url);
        }
      },
      error: () => {},
    });
    if (this.additionalNotes) {
      this.additionalNotesError = false;
      const Notespayload: INotes = {
        roleName: null,
        projectId: this.projectId,
        text: this.additionalNotes,
      };
      this.precastService
        ?.addNotes(Notespayload)
        ?.pipe(takeUntil(this.destroyed$))
        ?.subscribe({
          next: (response: INotes) => {
            this.snackBar.open(messagesConfig.notesSubmitSuccessMessage.message, 'DONE', {
              duration: 3000,
              verticalPosition: 'top',
              panelClass: ['default-snackbar'],
            });
            this.response = response;
            this.fetchNotesData(this.projectId);
          },
          error: () => {},
        });
    } else {
      this.additionalNotesError = true;
    }
  }

  cancel() {
    this.pageLeaveDialogRef = this.dialog?.open(ConfirmationDialogComponent, {
      panelClass: 'Accounting',
      data: {
        title: 'Cancel Changes',
        content: 'Are you sure you want to cancel? Your Changes will not be saved!',
        cancelText: 'NO, CONTINUE',
      },
      disableClose: true,
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    });
    this.pageLeaveDialogRef?.afterClosed()?.subscribe((response?: string) => {
      if (response === 'YES') {
        this.router.navigate(['/dashboard']);
      }
    });
  }

  showUpdatePopUpMessage(successMessage: IMessage, routeTo: string): void {
    this.updateSuccess = this.dialog.open(MessageModalComponent, {
      panelClass: 'app-dialog',
      data: successMessage,
      disableClose: true,
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    });
    this.updateSuccess.afterClosed().subscribe(() => {
      if (routeTo) {
        this.sharedService.routeValue(routeTo);
      }
    });
  }

  fetchNotesData(projectID: string): void {
    // Initiates the fetching of notes for the provided project ID
    this.precastService
      ?.getNotesbyId(projectID) // Calls the service to get notes by project ID
      ?.pipe(takeUntil(this.destroyed$)) // Ensures subscription is cancelled when component is destroyed
      ?.subscribe({
        next: (response: INotes[]) => {
          this.notes = response; // Updates the local state with the fetched notes
          this.additionalNotes = ''; // Resets additional notes
        },
        error: () => {
          // Error handling can be implemented here
        },
      });
  }

  // isRateExcluded(rateName: string): boolean {
  //   const excludedRates = [
  //     'Lab Unit Rate',
  //     'Mob/Demob Rate',
  //     'Field Testing Rate',
  //   ];
  //   return excludedRates.includes(rateName);
  // }

  onNonBillableToggle(event: any) {
    this.form.get('isNonBillable')?.setValue(event.checked);
    this.isNonBillable = event.checked;
    this.cdRef.detectChanges();
  }

  allowOnlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  decimalFilter(event: any) {
    // Regex pattern to ensure the format is x.xxx (up to 4 digits before the decimal, and exactly 2 after)
    const reg = /^\d{1,4}(\.\d{0,2})?$/;
    const inputValue = event.target.value + String.fromCharCode(event.charCode);
    // Ensure the input matches the format
    if (!reg.test(inputValue)) {
      event.preventDefault(); // Prevent invalid input
    }
  }

  public datechange(data: Date): void {
    const selectedDateTime = DateTimeUtils.setTimeAsEndOfTheDayWhenUserSelectValueFromDateOnlyPicker(data);
    // Update the invoiceDate FormControl value directly
    this.form.get('invoiceDate')?.setValue(selectedDateTime);
  }
}
